// ── Swiss Pairing Engine ─────────────────────────────────────────────
// Standard Swiss-system approach: sort by score (tiebreak rating), pair
// adjacent players who haven't already met, balance colors by who has
// had fewer whites, and hand out a single bye to an odd player out.

export function generateSwissPairings(participants) {
  // participants: [{ id, name, rating, score, opponents: [ids],
  //                   colorHistory: {w,b}, byes }]
  const pool = [...participants].sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return (b.rating || 0) - (a.rating || 0);
  });

  const pairings = [];
  const used = new Set();
  let byePlayer = null;

  if (pool.length % 2 === 1) {
    for (let i = pool.length - 1; i >= 0; i--) {
      if (!used.has(pool[i].id) && (pool[i].byes || 0) === 0) {
        byePlayer = pool[i];
        used.add(pool[i].id);
        break;
      }
    }
    if (!byePlayer) {
      byePlayer = pool[pool.length - 1];
      used.add(byePlayer.id);
    }
  }

  for (let i = 0; i < pool.length; i++) {
    const p1 = pool[i];
    if (used.has(p1.id)) continue;
    used.add(p1.id);

    let opponent = null;
    for (let j = i + 1; j < pool.length; j++) {
      const p2 = pool[j];
      if (used.has(p2.id)) continue;
      if ((p1.opponents || []).includes(p2.id)) continue;
      opponent = p2;
      break;
    }
    if (!opponent) {
      for (let j = i + 1; j < pool.length; j++) {
        const p2 = pool[j];
        if (used.has(p2.id)) continue;
        opponent = p2;
        break;
      }
    }
    if (!opponent) break;
    used.add(opponent.id);

    const p1Whites = p1.colorHistory?.w || 0;
    const p2Whites = opponent.colorHistory?.w || 0;
    const p1IsWhite = p1Whites <= p2Whites;
    const white = p1IsWhite ? p1 : opponent;
    const black = p1IsWhite ? opponent : p1;

    pairings.push({
      player1Id: white.id, player1Name: white.name, player1Rating: white.rating || 1200,
      player2Id: black.id, player2Name: black.name, player2Rating: black.rating || 1200,
      isBye: false,
    });
  }

  if (byePlayer) {
    pairings.push({
      player1Id: byePlayer.id, player1Name: byePlayer.name, player1Rating: byePlayer.rating || 1200,
      player2Id: null, player2Name: null,
      isBye: true,
    });
  }

  return pairings;
}

/** Suggested number of Swiss rounds for a given field size. */
export function recommendedSwissRounds(participantCount) {
  return Math.max(3, Math.ceil(Math.log2(Math.max(2, participantCount))));
}

/** Standings sorted by score, then Buchholz (sum of opponents' scores), then rating. */
export function computeStandings(participants) {
  const byId = {};
  participants.forEach(p => { byId[p.id] = p; });
  return [...participants]
    .map(p => {
      const buchholz = (p.opponents || []).reduce(
        (sum, oppId) => sum + (byId[oppId]?.score || 0), 0
      );
      return { ...p, buchholz };
    })
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if (b.buchholz !== a.buchholz) return b.buchholz - a.buchholz;
      return (b.rating || 0) - (a.rating || 0);
    });
}

/** Standard bracket seeding for knockout: 1 vs N, 2 vs N-1, etc. */
export function generateKnockoutPairings(seededPlayers) {
  const n = seededPlayers.length;
  const pairings = [];
  for (let i = 0; i < n / 2; i++) {
    const p1 = seededPlayers[i];
    const p2 = seededPlayers[n - 1 - i];
    pairings.push({
      player1Id: p1.id, player1Name: p1.name, player1Rating: p1.rating || 1200,
      player2Id: p2.id, player2Name: p2.name, player2Rating: p2.rating || 1200,
      isBye: false,
    });
  }
  return pairings;
}

/** Nearest power of 2 at or below `count`, capped at a sane max for a bracket. */
export function nearestBracketSize(count) {
  const options = [2, 4, 8, 16, 32, 64, 128];
  let best = 2;
  for (const o of options) {
    if (o <= count) best = o;
  }
  return best;
}
